import { parseDateOnly, startOfDay } from './assigned-queue';
import { daysBetween, endOfLocalWeek } from './dates';

export type WhenGroupId = 'late' | 'today' | 'tomorrow' | 'this_week' | 'next_week' | 'later' | 'no_date';

export const WHEN_GROUP_TITLES: Record<WhenGroupId, string> = {
  late: 'Overdue',
  today: 'Today',
  tomorrow: 'Tomorrow',
  this_week: 'This week',
  next_week: 'Next week',
  later: 'Later',
  no_date: 'No due date',
};

const ORDER: WhenGroupId[] = ['late', 'today', 'tomorrow', 'this_week', 'next_week', 'later', 'no_date'];

export function whenGroup(dueDate: string | null, today: Date): WhenGroupId {
  const due = dueDate ? parseDateOnly(dueDate) : null;
  if (!due) return 'no_date';
  const days = daysBetween(today, due);
  if (days < 0) return 'late';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  const endOfWeek = endOfLocalWeek(startOfDay(today));
  if (due <= endOfWeek) return 'this_week';
  const endOfNextWeek = new Date(endOfWeek.getFullYear(), endOfWeek.getMonth(), endOfWeek.getDate() + 7);
  if (due <= endOfNextWeek) return 'next_week';
  return 'later';
}

/** Items in the order given, split by when they are due. Empty groups are left out. */
export function groupByWhen<T extends { due_date: string | null }>(
  items: T[],
  today: Date,
): Array<{ id: WhenGroupId; title: string; items: T[] }> {
  const buckets = new Map<WhenGroupId, T[]>();
  for (const item of items) {
    const id = whenGroup(item.due_date, today);
    buckets.set(id, [...(buckets.get(id) ?? []), item]);
  }
  return ORDER.filter((id) => buckets.has(id)).map((id) => ({
    id,
    title: WHEN_GROUP_TITLES[id],
    items: buckets.get(id) ?? [],
  }));
}

export type BlockerSummary = {
  /** What is blocking it, as the person who marked it wrote it. */
  reason: string;
  /** Who it is waiting on, e.g. "Charles" or "you and Dan"; null when nobody is named. */
  waitingOn: string | null;
  /** Whole days since it was marked blocked. */
  days: number | null;
  /** What needs to happen next, from the reader's side. */
  next: string;
};

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
}

export function blockerSummary(
  item: { blocked_reason?: string | null; blocked_by_ids?: string[]; blocked_by_id?: string | null; blocked_at?: string | null },
  nameOf: (userId: string) => string | null,
  me: string | null,
  today: Date = new Date(),
): BlockerSummary | null {
  const reason = item.blocked_reason?.trim().replace(/^blocked\s*[:-]\s*/i, '');
  if (!reason) return null;
  const ids = item.blocked_by_ids?.length ? item.blocked_by_ids : item.blocked_by_id ? [item.blocked_by_id] : [];
  const waitingOnMe = me !== null && ids.includes(me);
  const others = ids.filter((id) => id !== me).map(nameOf).filter((name): name is string => Boolean(name));
  const names = waitingOnMe ? ['you', ...others] : others;
  const since = item.blocked_at ? new Date(item.blocked_at) : null;
  const days = since && !Number.isNaN(since.getTime())
    ? Math.max(0, daysBetween(startOfDay(since), startOfDay(today)))
    : null;
  const next = waitingOnMe
    ? 'it needs your answer before it can move'
    : others.length > 0
      ? `follow up with ${joinNames(others)}`
      : 'say who it is waiting on';
  return { reason, waitingOn: names.length > 0 ? joinNames(names) : null, days, next };
}
